import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ProgressBar } from '../shared/ui/ProgressBar';
import { uiBus } from '../lib/uiBus';

/**
 * LoadingContext — global loading bar state only.
 *
 * Isolated from NotificationContext so that progress bar changes
 * do NOT re-render toast consumers, and vice versa.
 */

interface LoadingContextType {
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoadingState] = useState(false);

  const setLoading = useCallback((val: boolean) => {
    setLoadingState(val);
  }, []);

  // Subscribe to event bus (called by API interceptors without direct context access)
  useEffect(() => {
    const unsubscribe = uiBus.subscribeLoading(setLoading);
    return unsubscribe;
  }, [setLoading]);

  return (
    <LoadingContext.Provider value={{ loading, setLoading }}>
      {loading && <ProgressBar />}
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
